import { Component, OnInit, Inject, forwardRef, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { ActivatedRoute } from '@angular/router';
import { AdminApiHandlerService } from '../services/api-handler/api-handler.service';
import { AppState } from '../../store/state.model';
import { Subscription, Observable } from 'rxjs';
import { SelectedVehicleState } from '../../store/selected-vehicle-store/selected-vehicle-state.model';
import { FinegrainRolesService } from '../../common/services/finegrain-roles/finegrain-roles.service';
import { CustomerState } from '../../store/customer/customer-state.model';
import { OrchestrationService } from '../services/orchestration-service/orchestration.service';
import { ApiResponseState } from '../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../store/api-response/api-response-actions';
import { StepState } from '../../store/steps-store/step-state.model';
import { StepActions } from '../../store/steps-store/step-actions';
import { provisioningCardEligibleGenerations } from '../../common/constants/constant';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { ResultCustomerComponent } from '../acm/result-customer/result-customer.component';
import { VehiclesListComponent } from '../subscription/vehicles-list/vehicles-list.component';
import { SurvivorAccountComponent } from '../acm/survivor-account/survivor-account.component';
import { RemoteStatusComponent } from '../subscription/remote-status/remote-status.component';
import { DigitalKeyComponent } from '../subscription/digital-key/digital-key.component';
import { ProvisioningComponent } from '../provisioning/provisioning.component';
import { RentalEvComponent } from '../rental-ev/rental-ev.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-search-results',
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.scss'],
    imports: [NgIf, MatIcon, ResultCustomerComponent, VehiclesListComponent, SurvivorAccountComponent, RemoteStatusComponent, DigitalKeyComponent, ProvisioningComponent, RentalEvComponent, TranslatePipe]
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  /**
  * Declaration of the route paramets
  **/
  public routeParams: object;
  /**
   * property to store the vehicle data
   */
  public vehicle: object;
  /**
   * property to subscribe to redirection
  **/
  private sub: Subscription;
  /**
   * property to store the selected vehicle subscriber
   */
  private selectedVehicleSubscriber: Subscription;
  /**
  *@arg selectedVehicleObservable to observe selectedVehicle observable
  **/
  @select('selectedVehicle') readonly selectedVehicleObservable: Observable<SelectedVehicleState>;
  /**
  * property to store the customer subscriber
  */
  private customerSubscription: Subscription;
  /**
  * Property to observe the redux customer store
  **/
  @select('customer') readonly customerObservable: Observable<CustomerState>;
  /**
  * Property to determine customer not found error
  **/
  public customerNotFoundError: boolean;
  /**
  * Property to store customer information
  */
  public customerData: any;
  /**
  *@arg  observe api variable in redux
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
  *@arg  observe api variable in redux
  **/
  @select('step') readonly stepObservable: Observable<StepState>;
  /**
   * @property used to store the  subscription
   */
  public subscription: Subscription;
  /**
   * @property used to store the boolean flag for displaying the rental ev card
   */
  public showRentalEvCard: boolean;
  /**
   * @property used to store the customer guid from vehicle association
   */
  public customerGuid: string;
  /**
   * @property used to store the step store subscription
   */
  public stepStoreSubscription: Subscription;

  /**
   *@property to identify the superuser based on role.
   **/
  public isSuperUser: boolean;
  /**
   *@property isAdmin Indicates whether the current user has administrative privileges.This is determined by checking if the user has the 'retryProvision' permission,
   * which corresponds to having either the 'TSTAdmin' base role or the 'OP_ADMIN' sub-role.
   * @public
   * @type {boolean}
   */
  public isAdmin: boolean;

  /**
   * @property used to check vehicle is subject to SB1394
   */
  public isSBCompliant = false;


  constructor(
    @Inject(forwardRef(() => AdminApiHandlerService)) public apiHandler: AdminApiHandlerService,
    public ngRedux: NgRedux<AppState>,
    public route: ActivatedRoute,
    public fineGrainRolesService: FinegrainRolesService,
    public orch: OrchestrationService
  ) {
    this.subscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_FIND_VEHICLE && api.findVehicle['type'] === 'success' && !!api.findVehicle['payload']['vehicle']){
        this.isSBCompliant = !!api.findVehicle['payload']['vehicle']['complianceFlags'] && api.findVehicle['payload']['vehicle']['complianceFlags']['SB1394'];
        if(this.isSBCompliant) {this.orch.getSurvivorDetails(api.findVehicle['payload']['vehicle']);}
      } else if (api.apiAction === ApiResponseActions.SET_FIND_VEHICLE && api.findVehicle['type'] === 'error'){
        this.isSBCompliant = false;
      }
    });
    this.stepStoreSubscription = this.stepObservable.subscribe((step) => {
      if(step.apiAction === StepActions.STORE_GUID_VALUE){
        this.customerGuid = !!step.storeGuidValue ? step.storeGuidValue : '';
      };
    });
  }
  /**
  * @method ngOnInit
  * Initializes the component and sets up subscriptions for vehicle and customer observables.
  */
  ngOnInit(): void {
    this.showRentalEvCard = false;
    this.isSBCompliant = false;
    this.selectedVehicleSubscriber = this.selectedVehicleObservable.subscribe(({ vehicle }) => {
      this.vehicle = vehicle || {};
      // Safely extract modelCode without optional chaining / nullish coalescing
      const vehicledetail: any = this.vehicle;
      // Show card only when VIN present and model eligible
      this.showRentalEvCard = !!(vehicledetail.vin && (vehicledetail.subscriberGuid || this.customerGuid));
      // Capture profile route parameter for downstream components
      this.routeParams = { profile: this.ngRedux.getState().common.profile };
      // Ensure subscriber GUID is populated (only if not explicitly null)
      if (vehicledetail.vin && vehicledetail.subscriberGuid !== null) {
        this.vehicle['subscriberGuid'] = this.vehicle['subscriberGuid'] || this.customerGuid;
      }
    });
    /**
    * Subscribe to customer store for customer state updates and error tracking.
    */
    this.customerSubscription = this.customerObservable.subscribe((customer) => {
      this.customerNotFoundError = customer.error_customer_not_found;
      this.customerData = customer;
    });
    this.setSuperUserFlag();
    this.setAdminUserFlag();
  }

  /**
   * Sets the isSuperUser flag based on whether the user has the 'modifyDcm' permission.
   * The 'modifyDcm' permission is granted if the user has the 'SUPER_USER' role.
   */
  setSuperUserFlag(): void {
    this.isSuperUser = this.fineGrainRolesService.getCustomPermissions('modifyDcm');
  }

  /**
   * Sets the isAdmin flag based on whether the user has the 'retryProvision' permission.The 'retryProvision' permission is granted if the user has the 'TSTAdmin' base role
   * or the 'OP_ADMIN' sub-role.
   */

  setAdminUserFlag(): void {
    this.isAdmin = this.fineGrainRolesService.getCustomPermissions('retryProvision');
  }


  /**
   * @function ngOnDestroy Method used to unsubscribe the subscribed subscribers
   */
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.selectedVehicleSubscriber) {
      this.selectedVehicleSubscriber.unsubscribe();
    }
    if (this.customerSubscription) {
      this.customerSubscription.unsubscribe();
    }
  }
  /**
  * @function displayResults Determines to display result or not
  **/
  public displayResults(): boolean {
    return !!this.vehicle && this.vehicle['vin'];
  }
  /**
  * @function displayDigitalKey Determines to display digital card or not based on it's generation and any of the following capability ['DigitalKeyEnhanced', 'DigitalKeyFactory', 'DigitalKeySKB']
  **/
  public displayDigitalKey(): boolean {
    return !!this.vehicle && this.vehicle['generation'] === '21MM';
  }
  /**
  * @function displayProvisioning Determines to display result or not
  **/
  public displayProvisioning(): boolean {
    return !!this.vehicle['generation'] ? this.displayResults() &&
      this.vehicle['generation'] &&
      (provisioningCardEligibleGenerations.includes(this.vehicle['generation'].toLowerCase())) : false;
  }

  /**
  * @function displayCustomerInfo validates subscription of vehicle
  **/
  public displayCustomerInfo() {
    let flag = (this.vehicle && this.vehicle['subscriberGuid']) ? true : false;
    if (!flag && this.customerData && this.customerData.data && this.customerData.data.guid) {
      flag = true;
    }
    return flag;
  }

  /**
  * @function getCvtFlag gets the boolean value if the vehicle is nonCvt
  **/
  public getCvtFlag() {
    if (this.vehicle['nonCvtVehicle'] && this.routeParams['profile'] === 'customer') {
      return true;
    } else if (this.vehicle['nonCvtVehicle'] && this.routeParams['profile'] === 'vehicle') {
      return true;
    }
    return true;
  }
}
